/**
 * The Report Detail page's address: `/registry/:reportId`, with its tab and its
 * window in the query string so a shared link opens what the sender was
 * looking at.
 *
 * Seven read-only tabs, and no Lineage: lineage needs semantic models that do
 * not exist. The defaults (Overview, 30 days) are left out of the URL, so the
 * plain link a list renders is the canonical one.
 *
 * The windows stop at 90 days because that is what retention keeps by default
 * (`ReportRun` is trimmed after `Cartographer:Governance:ReportRunRetentionDays`,
 * default 90): a longer window would promise history that no longer exists.
 */

export const REPORT_DETAIL_TABS = [
  'overview',
  'governance',
  'operations',
  'usage',
  'scheduling',
  'access',
  'history',
] as const

export type ReportDetailTab = (typeof REPORT_DETAIL_TABS)[number]

/** The run-history windows the page offers, in days. */
export const REPORT_DETAIL_WINDOWS = [7, 30, 90] as const

export type ReportDetailWindow = (typeof REPORT_DETAIL_WINDOWS)[number]

const DEFAULT_TAB: ReportDetailTab = 'overview'
const DEFAULT_DAYS: ReportDetailWindow = 30

/**
 * `/registry/<id>`, plus `?tab=` when the tab is not Overview and `days=` when
 * the window is not 30. The id is encoded: it comes from the server, and a
 * path is not the place to trust it.
 */
export function reportDetailPath(reportId: string, tab?: ReportDetailTab, days?: number): string {
  const params = new URLSearchParams()
  if (tab && tab !== DEFAULT_TAB) params.set('tab', tab)
  if (days !== undefined && days !== DEFAULT_DAYS) params.set('days', String(days))
  const query = params.toString()
  return `/registry/${encodeURIComponent(reportId)}${query ? `?${query}` : ''}`
}

/** The tab a `?tab=` value names; anything unknown (or absent) opens Overview. */
export function openReportDetailTab(value: string | null | undefined): ReportDetailTab {
  return (REPORT_DETAIL_TABS as readonly string[]).includes(value ?? '') ? (value as ReportDetailTab) : DEFAULT_TAB
}

/** The window a `?days=` value names; anything other than exactly 7, 30 or 90 is 30. */
export function parseReportDetailDays(value: string | null | undefined): ReportDetailWindow {
  return REPORT_DETAIL_WINDOWS.find((days) => String(days) === value) ?? DEFAULT_DAYS
}
